import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, X } from "lucide-react";
import { EmSwash } from "../EmSwash";
import { Gems } from "../Gems";

/**
 * PayoutReturnCard — the warm "welcome back from Stripe" letter card, shown
 * above the payout card after ?payouts=return until the student dismisses it.
 * Copy follows the live Connect state so it flips to "You're connected." the
 * moment verification clears. Founder pick A (2026-09-06).
 */
const COPY = {
  ready: {
    tone: "grass",
    eyebrow: "Stripe connected",
    title: <>You're <EmSwash color="text-grass">connected.</EmSwash></>,
    body: "Grants from My Rich Aunt can now reach you. When your first grant is sent it lands in your bank account in about two business days, and we'll email you the moment it goes out.",
    secondary: "Open Stripe payout dashboard",
  },
  in_review: {
    tone: "cobalt",
    eyebrow: "Almost there",
    title: <>Stripe is checking a <EmSwash color="text-cobalt">few details.</EmSwash></>,
    body: "That usually clears within a day, and there's nothing more for you to do. This card updates itself, and grants can be issued the moment it turns green.",
    secondary: null,
  },
  unfinished: {
    tone: "cobalt",
    eyebrow: "Welcome back",
    title: <>Not quite <EmSwash color="text-cobalt">finished.</EmSwash></>,
    body: "Stripe still needs a few details before grants can reach you. Your progress is saved, so continue whenever you're ready; it only takes a few minutes.",
    secondary: null,
  },
};

const variantFor = (state) => (state === "ready" ? "ready" : state === "in_review" ? "in_review" : "unfinished");

export const PayoutReturnCard = ({ state, onDismiss, onBack, onSecondary }) => {
  const v = variantFor(state);
  const c = COPY[v];
  const grass = c.tone === "grass";
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-testid="payout-return-card"
      data-variant={v}
      className={`relative mt-6 overflow-hidden rounded-xl border-2 bg-bone p-6 pr-14 ${grass ? "border-grass" : "border-cobalt"}`}
    >
      <button type="button" onClick={onDismiss} aria-label="Dismiss" data-testid="payout-return-dismiss"
        className="absolute right-4 top-4 text-olive transition-colors hover:text-ink">
        <X className="h-4 w-4" strokeWidth={2.2} />
      </button>
      <span className={`text-[13px] font-semibold uppercase tracking-[0.22em] ${grass ? "text-grass" : "text-cobalt"}`}>{c.eyebrow}</span>
      <h3 className="mt-2 font-serif text-3xl leading-[1.05] text-ink" data-testid="payout-return-title">{c.title}</h3>
      <p className="mt-3 max-w-xl text-base leading-relaxed text-olive">{c.body}</p>
      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button type="button" onClick={onBack} data-testid="payout-return-back"
          className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-base font-semibold text-sand transition-colors hover:bg-forest">
          Back to your campaign <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </button>
        {c.secondary && (
          <button type="button" onClick={onSecondary} data-testid="payout-return-secondary"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[0.18em] text-olive transition-colors hover:text-ink">
            {c.secondary} <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        )}
      </div>
      <Gems className="mt-6" />
    </motion.div>
  );
};
